import React, { useRef, useState, useCallback } from "react";
import { ArrowUp, Info, Loader2 } from "lucide-react";

interface ChatInputProps {
  isLoading: boolean;
  onSend: (message: string) => void;
  messageSending: boolean;
  disabled: boolean;
}

const ChatInput = React.memo(
  ({ isLoading, onSend, messageSending, disabled }: ChatInputProps) => {
    const [input, setInput] = useState<string>("");
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInput(e.target.value);
        e.target.style.height = "inherit";
        e.target.style.height = `${e.target.scrollHeight}px`;
      },
      []
    );

    const handleSend = useCallback(() => {
      if (input.trim() && !disabled) {
        onSend(input);
        setInput("");
        if (textareaRef.current) {
          textareaRef.current.style.height = "inherit";
        }
      }
    }, [input, onSend, disabled]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey && !disabled) {
          e.preventDefault();
          handleSend();
        }
      },
      [handleSend, disabled]
    );

    return (
      <div className="px-4 py-2 text-[12.5px] rounded-b-lg">
        <p
          className={`text-xs text-neutral-500 font-light text-center px-2 py-2 transition-opacity duration-300 ${
            isFocused ? "opacity-90" : "opacity-50"
          }`}
        >
          <Info className="w-3.5 h-3.5 inline-block mr-1.5 transform -translate-y-[1px]" />
          Tora is still in beta. Always double check output before making
          decisions.
        </p>
        <div className="flex rounded-full shadow-sm ring-1 ring-neutral-300 pl-3 pr-2 items-center">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder="Chat with Tora..."
            className="flex-1 p-3 max-h-36 rounded-l-lg focus:outline-none resize-none bg-transparent"
            rows={1}
          />
          <button
            onClick={handleSend}
            disabled={isLoading || input.trim() === "" || disabled}
            className={`w-8 h-8 flex items-center justify-center rounded-full bg-indigo-700 hover:bg-indigo-800 text-white transition duration-500 ease-in-out focus:outline-none ${
              disabled ? "opacity-50" : ""
            }`}
          >
            {messageSending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ArrowUp className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    );
  }
);

ChatInput.displayName = "ChatInput";

export default ChatInput;
